import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import { getMorningReports, createMorningReport, extractErrorMessage } from '../services/api';
import { FaPlus, FaSun } from 'react-icons/fa';
import UserSelect from '../components/UserSelect';
import { useToast } from '../components/Toast';

function MorningReports({ user }) {
  const toast = useToast();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [newReport, setNewReport] = useState({
    user_id: '',
    mood: 5,
    sleep_quality: 5,
    woke_at: '',
    notes: ''
  });

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      setLoading(true);
      const data = await getMorningReports();
      setReports(data.reports || data);
    } catch (error) {
      console.error('Error fetching morning reports:', error);
      toast.error(extractErrorMessage(error, 'Morgenberichte konnten nicht geladen werden'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    setFormError(null);
    try {
      await createMorningReport(newReport);
      setNewReport({ user_id: '', mood: 5, sleep_quality: 5, woke_at: '', notes: '' });
      setShowModal(false);
      toast.success('Morgenbericht gespeichert');
      fetchReports();
    } catch (error) {
      console.error('Error creating morning report:', error);
      setFormError(extractErrorMessage(error, 'Fehler beim Erstellen des Morgenberichts'));
    } finally {
      setSaving(false);
    }
  };

  const closeModal = () => {
    if (saving) return;
    setShowModal(false);
    setFormError(null);
  };

  const getMoodEmoji = (mood) => {
    if (mood >= 8) return '😊';
    if (mood >= 6) return '🙂';
    if (mood >= 4) return '😐';
    if (mood >= 2) return '😔';
    return '😢';
  };

  const getSleepQualityLabel = (quality) => {
    if (quality >= 8) return 'Excellent';
    if (quality >= 6) return 'Good';
    if (quality >= 4) return 'Fair';
    if (quality >= 2) return 'Poor';
    return 'Terrible';
  };

  return (
    <div className="app-layout">
      <Navbar user={user} />
      <main className="main-content">
        <div className="page-header">
          <h1><FaSun /> Morning Reports</h1>
          <button className="add-btn" onClick={() => setShowModal(true)}>
            <FaPlus /> New Morning Report
          </button>
        </div>

        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
          </div>
        ) : (
          <div className="data-table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Mood</th>
                  <th>Sleep Quality</th>
                  <th>Woke At</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {reports.length > 0 ? (
                  reports.map(report => (
                    <tr key={report.id}>
                      <td>
                        {new Date(report.created_at).toLocaleDateString('de-DE', {
                          weekday: 'short', day: 'numeric', month: 'short', year: 'numeric'
                        })}
                      </td>
                      <td>{getMoodEmoji(report.mood)} {report.mood}/10</td>
                      <td>{report.sleep_quality}/10 ({getSleepQualityLabel(report.sleep_quality)})</td>
                      <td>{report.woke_at || '-'}</td>
                      <td>{report.notes || '-'}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="no-data">No morning reports found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {showModal && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
              <h2>New Morning Report</h2>
              {formError && <div className="shared-form-error-banner">{formError}</div>}
              <form onSubmit={handleCreate}>
                <div className="form-group">
                  <UserSelect
                    value={newReport.user_id}
                    onChange={id => setNewReport({...newReport, user_id: id})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Mood: {newReport.mood}/10 {getMoodEmoji(newReport.mood)}</label>
                  <input
                    type="range"
                    min="1"
                    max="10"
                    value={newReport.mood}
                    onChange={e => setNewReport({...newReport, mood: parseInt(e.target.value)})}
                    className="slider"
                  />
                </div>
                <div className="form-group">
                  <label>Sleep Quality: {newReport.sleep_quality}/10</label>
                  <input
                    type="range"
                    min="1"
                    max="10"
                    value={newReport.sleep_quality}
                    onChange={e => setNewReport({...newReport, sleep_quality: parseInt(e.target.value)})}
                    className="slider"
                  />
                </div>
                <div className="form-group">
                  <label>Woke At</label>
                  <input
                    type="time"
                    value={newReport.woke_at}
                    onChange={e => setNewReport({...newReport, woke_at: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Notes</label>
                  <textarea
                    value={newReport.notes}
                    onChange={e => setNewReport({...newReport, notes: e.target.value})}
                    placeholder="How are you feeling this morning?"
                  />
                </div>
                <div className="modal-actions">
                  <button type="button" className="cancel-btn" onClick={closeModal} disabled={saving}>Cancel</button>
                  <button type="submit" className="submit-btn" disabled={saving}>{saving ? 'Speichert…' : 'Submit'}</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default MorningReports;
